import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { ChatProvider } from './ChatContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import AdminLayout from './components/AdminLayout';
import StagingBanner from './components/StagingBanner';
import SupportSessionBanner from './components/SupportSessionBanner';
import ProdBanner from './components/ProdBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { IS_PROD, IS_STAGING } from './config';

// Company Portal Pages
import Login from './pages/Login';
import SsoCallback from './pages/SsoCallback';
import AdminOAuthCallback from './pages/AdminOAuthCallback';
import Home from './pages/Home';
import PromptGuide from './pages/PromptGuide';
import Reports from './pages/Reports';
import SourcingEvents from './pages/sourcing/SourcingEvents';
import SourcingEventWorkspace from './pages/sourcing/SourcingEventWorkspace';
import Quoting from './pages/quoting/Quoting';
import MaterialList from './pages/MaterialList';
import MaterialDossier from './pages/MaterialDossier';
import ManageContracts from './pages/ManageContracts';
import Analytics from './pages/Analytics';
import Suppliers from './pages/Suppliers';
import SupplierDetail from './pages/SupplierDetail';
import Accounts from './pages/Accounts';
import Customers, { LegacyAccountRedirect } from './pages/Customers';
import MeetingPrepDeck from './pages/MeetingPrepDeck';
import Profile from './pages/Profile';
import InterviewWizard from './pages/InterviewWizard';
import InterviewPortal from './pages/InterviewPortal';
import MyCaptures from './pages/MyCaptures';
import Opportunities from './pages/Opportunities';
import InventoryWorklist from './pages/InventoryWorklist';
import OpportunityFinding from './pages/OpportunityFinding';

// Admin Portal Pages
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import CompanyList from './pages/CompanyList';
import CompanyDetail from './pages/CompanyDetail';
import AdminKnowledge from './pages/AdminKnowledge';
import AdminUsers from './pages/AdminUsers';
import AdminCronRuns from './pages/AdminCronRuns';
import AdminChatOutcomes from './pages/AdminChatOutcomes';
import AdminDemoRequests from './pages/AdminDemoRequests';

// Auth Pages (public)
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Supplier Portal Pages

// Sourcing events supplier portal (Lane 1331). A separate app area with its
// own token and shell; it never uses the buyer AuthContext.
import SourcingPortalJoin from './pages/sourcing-portal/JoinPage';
import SourcingPortalLogin from './pages/sourcing-portal/LoginPage';
import SourcingPortalHome from './pages/sourcing-portal/HomePage';
import SourcingPortalEvent from './pages/sourcing-portal/EventPage';

import './App.css';

// HB-17 / §5.BV — env guardrails: tab-title prefix + favicon swap.
// Runs once on mount; brief FOUC of base title + dev favicon before swap
// is acceptable since operators read tabs after page loads, not mid-render.
// Local dev (neither env set) keeps the base title + dev favicon defaults.
function useEnvGuardrails() {
  useEffect(() => {
    if (IS_PROD) {
      document.title = '[PROD] Wingman';
    } else if (IS_STAGING) {
      document.title = '[STAGING] Wingman';
    }

    if (IS_PROD || IS_STAGING) {
      const href = IS_PROD ? '/favicon-prod.svg' : '/favicon-staging.svg';
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/svg+xml';
        document.head.appendChild(link);
      }
      link.href = href;
    }
  }, []);
}

// The old RFP builder is retired (Lane 1369). An old /rfp link goes to
// Sourcing events when the company has it (/sourcing is only registered then),
// otherwise Home. Right after a fresh sign-in /me is still loading and every
// flag reads false, so wait for it (render nothing) rather than send a
// Sourcing company to Home. `meFlagsResolved === false` is checked strictly:
// only the real AuthContext reports it, and a missing value means "known".
export function RetiredRfpRedirect() {
  const { sourcingEventsEnabled, meFlagsResolved } = useAuth();
  if (meFlagsResolved === false) return null;
  return <Navigate to={sourcingEventsEnabled ? '/sourcing' : '/'} replace />;
}

// Company-portal route tree. Extracted from the inline JSX (Lane 42) so it can
// read AuthContext: the /opportunities routes are REGISTERED only when the
// env-level OPPORTUNITIES_ENABLED flag arrives via /me. When the flag is OFF a
// direct URL hit falls through to the same unmatched-route blank content as any
// unknown path — no Opportunities code mounts, no fetch fires. Exported for the
// flag-gating test.
export function CompanyPortalRoutes() {
  const { opportunitiesEnabled, inventoryOrderingEnabled,
          intelligencePortalEnabled, workbookExportEnabled,
          hasReportArtifacts, homeChatFirstLandingEnabled,
          sourcingEventsEnabled, quotingEnabled } = useAuth();
  return (
    <ChatProvider>
      <AppLayout>
        <Routes>
          {/* Main Pages */}
          <Route path="/" element={<Home />} />
          {/* Lane 1211: chat-first landing gate. Route registered only when
              the flag is on (ON staging / OFF PROD), so PROD is unchanged
              until the flag flips. The landing link is gated on the same flag. */}
          {homeChatFirstLandingEnabled &&
            <Route path="/prompt-guide" element={<PromptGuide />} />}
          <Route path="/materials" element={<MaterialList />} />
          <Route path="/materials/:id" element={<MaterialDossier />} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/suppliers/:supplierName" element={<SupplierDetail />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/accounts/:id" element={<LegacyAccountRedirect />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/contracts" element={<ManageContracts />} />
          <Route path="/analytics" element={<Analytics />} />
          {/* Reports (Lane 1132): registered when creation is on OR
              artifacts exist (read-only rollback — never strand files). */}
          {(workbookExportEnabled || hasReportArtifacts) &&
            <Route path="/reports" element={<Reports />} />}
          {/* Published authored decks remain reachable when creation flags are
              rolled back; mutation endpoints enforce the independent gate. */}
          <Route path="/decks/authored/:deckVersionId" element={<Navigate to="/decks/meeting-prep" replace />} />

          {/* Opportunities (Lane 42) — flag-gated route registration */}
          {opportunitiesEnabled && <Route path="/opportunities" element={<Opportunities />} />}
          {opportunitiesEnabled && <Route path="/opportunities/:findingId" element={<OpportunityFinding />} />}

          {/* Inventory & Ordering (Lane 500) — flag-gated route registration.
              Same shape as Opportunities: with INVENTORY_ORDERING_ENABLED OFF
              the route never registers, so a direct URL hit falls through to the
              unmatched-route blank content and no worklist code mounts and no
              fetch fires. The endpoint 404s independently (double OFF-lock). */}
          {inventoryOrderingEnabled && <Route path="/inventory" element={<InventoryWorklist />} />}

          {/* Sourcing events (Lane 1322): registered only when /me says the
              effective gate is on; every API route also answers 404 when off. */}
          {/* Quoting (Lane 1335): registered only when /me says the company's
              Quoting is live; every /api/rfqs route also answers 404 when off. */}
          {quotingEnabled && <Route path="/quoting" element={<Quoting />} />}
          {quotingEnabled && <Route path="/quoting/:rfqId" element={<Quoting />} />}
          {sourcingEventsEnabled && <Route path="/sourcing" element={<SourcingEvents />} />}
          {sourcingEventsEnabled && <Route path="/sourcing/:publicId" element={<SourcingEventWorkspace />} />}
          {/* The old RFP builder is retired from the buyer app (Lane 1369,
              Ryan 2026-10-03: "Get rid of the old RFP builder"). Sourcing
              events replace it. Every old /rfp page (list, AI builder, detail,
              bid analysis, negotiation deck, executive summary, snapshot) now
              goes to Sourcing events when the company has it, otherwise Home.
              The old page files left with Lane 1410 W2 and every /api/rfp
              endpoint and the old supplier portal with Lane 1410 W6; only the
              redirect remains. */}
          <Route path="/rfp/*" element={<RetiredRfpRedirect />} />
          <Route path="/decks/meeting-prep" element={<MeetingPrepDeck />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/interview" element={<InterviewWizard />} />
          {/*
            TWO paths, ONE component (Lane 826).

            `/intelligence` is the real home and the one the nav links to. It is
            flag-gated like its neighbours above, so with INTELLIGENCE_PORTAL_ENABLED
            off there is no nav item, no route, and the endpoint 404s anyway
            (triple OFF-lock).

            `/captures` stays registered UNCONDITIONALLY and is NOT flag-gated,
            deliberately. It is the target of the provenance deep link built at
            utils/provenanceChip.js (`/captures?candidate_id=X`) and asserted by
            Home.provenance.test.js — a live contract that predates this lane and
            must keep working whether or not the new page is switched on. The page
            reads `candidate_id` and resolves it to the `chat:<id>` entry.
          */}
          {intelligencePortalEnabled && (
            <Route path="/intelligence" element={<MyCaptures />} />
          )}
          <Route path="/captures" element={<MyCaptures />} />
        </Routes>
      </AppLayout>
    </ChatProvider>
  );
}

function App() {
  useEnvGuardrails();

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <StagingBanner />
          <ProdBanner />
          <SupportSessionBanner />
          <Routes>
          {/* Public routes - Login pages */}
          <Route path="/login" element={<Login />} />
          <Route path="/sso/callback" element={<SsoCallback />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/oauth/callback" element={<AdminOAuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Sourcing events supplier portal (Lane 1331) - public routes; the
              pages hold their own portal session. Every API route also answers
              404 while the feature is off. */}
          <Route path="/sourcing-portal/join" element={<SourcingPortalJoin />} />
          <Route path="/sourcing-portal/login" element={<SourcingPortalLogin />} />
          <Route path="/sourcing-portal" element={<SourcingPortalHome />} />
          <Route path="/sourcing-portal/events/:eventId" element={<SourcingPortalEvent />} />
          <Route path="/sourcing-portal/*" element={<Navigate to="/sourcing-portal" replace />} />

          {/* Admin Portal routes */}
          <Route path="/admin/*" element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <Routes>
                  <Route path="/" element={<AdminDashboard />} />
                  <Route path="/companies" element={<CompanyList />} />
                  <Route path="/companies/new" element={<CompanyList />} />
                  <Route path="/companies/:id" element={<CompanyDetail />} />
                  <Route path="/knowledge" element={<AdminKnowledge />} />
                  <Route path="/users" element={<AdminUsers />} />
                  <Route path="/chat-outcomes" element={<AdminChatOutcomes />} />
                  <Route path="/cron-runs" element={<AdminCronRuns />} />
                  <Route path="/demo-requests" element={<AdminDemoRequests />} />
                  <Route path="/knowledge-coverage" element={<Navigate to="/admin/companies" replace />} />
                </Routes>
              </AdminLayout>
            </ProtectedRoute>
          } />

          {/* Standalone Interview Portal (public — no auth required) */}
          <Route path="/interview/portal/:inviteCode" element={<InterviewPortal />} />

          {/* Company Portal routes */}
          <Route path="/*" element={
            <ProtectedRoute requireCompany>
              <CompanyPortalRoutes />
            </ProtectedRoute>
          } />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
